// Cart Page JavaScript - Full page cart (no drawer)
// - Auto-updates line totals + subtotal when quantity changes
// - Re-renders cart sections so Liquid + BSS B2B pricing hooks stay correct

(function () {
  'use strict';

  // IMPORTANT: Shopify's ?sections= API expects section INSTANCE ids from the template (cart.json),
  // not the section file names. Our cart.json uses "cart-items" and "cart-footer".
  const SECTION_IDS = ['cart-items', 'cart-footer'];
  const CART_URL = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
  // Debug logging (can be disabled by changing to empty function)
  function dbg() {}

  // ============================================================
  // SAFE JSON PARSER - Prevents "Unexpected end of JSON input" errors
  // ============================================================
  async function safeJson(res) {
    const text = await res.text();
    if (!text || text.trim() === '') {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      console.warn('[CART-PAGE] Non-JSON response received:', text.substring(0, 200));
      return { _nonJson: true, _raw: text.substring(0, 500) };
    }
  }

  function extractErrorMessage(data, status) {
    if (!data) return `Request failed with status ${status} (empty response)`;
    if (data._nonJson) return `Request failed with status ${status} (non-JSON response)`;
    if (data.description) return data.description;
    if (data.message) return data.message;
    if (data.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    return `Request failed with status ${status}`;
  }

  function logCartRequestFailure(endpoint, method, status, payload, responseData) {
    console.group(`[CART-PAGE] Request Failed: ${method} ${endpoint}`);
    console.log('HTTP Status:', status);
    if (payload) console.log('Request Payload:', payload);
    if (responseData) {
      if (responseData._nonJson) {
        console.log('Response (non-JSON):', responseData._raw);
      } else {
        console.log('Response Data:', responseData);
      }
    } else {
      console.log('Response: (empty)');
    }
    console.groupEnd();
  }

  function clampQty(value) {
    const n = parseInt(value, 10);
    if (Number.isNaN(n) || n < 0) return 0;
    return n;
  }

  function getLineInput(line) {
    return document.querySelector(`[data-cart-qty-input][data-line="${line}"]`);
  }

  function getMaxQty(line) {
    const input = getLineInput(line);
    if (!input) return 999999;
    const attr = input.getAttribute('data-max-qty') || input.getAttribute('max');
    const max = parseInt(attr || '0', 10);
    return Number.isFinite(max) && max >= 0 ? max : 999999;
  }

  /**
   * BACKORDER: Check if a line item is a backorder item
   * Backorder items have data-is-backorder="true" on the input or row
   */
  function isBackorderLine(line) {
    const input = getLineInput(line);
    if (input && input.dataset.isBackorder === 'true') return true;
    const row = document.querySelector(`.cart-page__row[data-line="${line}"]`);
    if (row && row.dataset.isBackorder === 'true') return true;
    return false;
  }

  function showQtyError(line, message) {
    const row = document.querySelector(`.cart-page__row[data-line="${line}"]`);
    const el = row ? row.querySelector('[data-qty-error]') : null;
    if (!el) return;
    if (!message) {
      el.textContent = '';
      el.style.display = 'none';
      return;
    }
    el.textContent = message;
    el.style.display = '';
  }

  function clampToMax(line, qty) {
    // BACKORDER: Don't clamp backorder items (they're untracked variants)
    if (isBackorderLine(line)) {
      showQtyError(line, '');
      return qty;
    }
    
    const max = getMaxQty(line);
    if (qty > max) {
      showQtyError(line, `Max available is ${max}.`);
      return max;
    }
    showQtyError(line, '');
    return qty;
  }

  async function changeLine(line, quantity) {
    const endpoint = '/cart/change.js';
    const payload = { line, quantity };
    dbg('H1', 'cart-page.js:changeLine', 'POST /cart/change.js', payload);
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const data = await safeJson(res);
      logCartRequestFailure(endpoint, 'POST', res.status, payload, data);
      throw new Error(extractErrorMessage(data, res.status));
    }
    dbg('H1', 'cart-page.js:changeLine', 'OK /cart/change.js', payload);
  }

  async function changeLineWithSections(line, quantity) {
    // Dawn-style: request sections in the cart_change response so we always get HTML strings back.
    // IMPORTANT: Shopify's `routes.cart_change_url` is often `/cart/change` (HTML response).
    // We need the JSON endpoint (`/cart/change.js`) to avoid reloads and parse errors.
    const baseChangeUrl =
      (window.routes && window.routes.cart_change_url) ? window.routes.cart_change_url : '/cart/change';
    const changeUrl = baseChangeUrl.endsWith('.js') ? baseChangeUrl : `${baseChangeUrl}.js`;

    const body = {
      line,
      quantity,
      sections: SECTION_IDS,
      sections_url: window.location.pathname
    };

    dbg('H2', 'cart-page.js:changeLineWithSections', 'POST cart_change_url with sections', { changeUrl, body });

    const res = await fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });

    dbg('H2', 'cart-page.js:changeLineWithSections', 'cart_change response', {
      ok: res.ok,
      status: res.status,
      contentType: res.headers && res.headers.get ? res.headers.get('content-type') : null
    });

    if (!res.ok) {
      const errorData = await safeJson(res);
      logCartRequestFailure(changeUrl, 'POST', res.status, body, errorData);
      throw new Error(extractErrorMessage(errorData, res.status));
    }

    // Some stores may still respond with HTML if misrouted; parse defensively using safeJson.
    // Note: We call safeJson to handle the response safely
    const state = await safeJson(res);
    if (!state || state._nonJson) {
      dbg('H5', 'cart-page.js:changeLineWithSections', 'failed to parse JSON (got non-JSON)', {
        changeUrl,
        textPreview: state?._raw?.slice(0, 120) || null
      });
      // Trigger fallback section fetch path in runUpdate instead of hard reloading.
      return { sections: null, errors: null };
    }
    dbg('H2', 'cart-page.js:changeLineWithSections', 'cart_change parsed keys', {
      keys: Object.keys(state || {}),
      sectionsKeys: state && state.sections ? Object.keys(state.sections) : null,
      itemCount: state && typeof state.item_count === 'number' ? state.item_count : null,
      hasErrors: !!(state && state.errors)
    });
    return state;
  }

  async function fetchSections() {
    // Shopify section rendering API should be called from the store root, not /cart,
    // otherwise some setups return cart JSON instead of section HTML.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    const url = `${root}?sections=${encodeURIComponent(SECTION_IDS.join(','))}`;
    dbg('H2', 'cart-page.js:fetchSections', 'GET section-rendering API', { url, sectionIds: SECTION_IDS, root });
    const res = await fetch(url, { method: 'GET' });
    const contentType = res.headers && res.headers.get ? res.headers.get('content-type') : null;
    dbg('H2', 'cart-page.js:fetchSections', 'sections response', { ok: res.ok, status: res.status, contentType });
    if (!res.ok) throw new Error('Failed to refresh cart');
    const json = await safeJson(res);
    if (!json || json._nonJson) throw new Error('Invalid sections response');
    dbg('H2', 'cart-page.js:fetchSections', 'sections keys', { keys: Object.keys(json || {}) });
    // Inspect value shapes for each requested section id (Shopify usually returns HTML strings, but some setups return objects)
    try {
      SECTION_IDS.forEach((id) => {
        const val = json ? json[id] : undefined;
        const t = typeof val;
        const keys = (val && t === 'object') ? Object.keys(val).slice(0, 25) : null;
        const preview =
          (t === 'string')
            ? val.slice(0, 120)
            : (val && t === 'object')
              ? JSON.stringify(val).slice(0, 120)
              : null;
        dbg('H2', 'cart-page.js:fetchSections', 'section value shape', { id, type: t, keys, preview });
      });
    } catch (_) {}
    return json;
  }

  function coerceSectionHtml(maybeHtml, sectionId) {
    // Shopify should return HTML strings, but if it returns an object, log and try common fields.
    const t = typeof maybeHtml;
    if (t === 'string') return maybeHtml;
    if (!maybeHtml || t !== 'object') return null;

    // Log the full structure for debugging
    try {
      dbg('H3', 'cart-page.js:coerceSectionHtml', 'Received object structure', { 
        sectionId, 
        keys: Object.keys(maybeHtml),
        fullObject: JSON.stringify(maybeHtml).substring(0, 500)
      });
    } catch (e) {
      dbg('H3', 'cart-page.js:coerceSectionHtml', 'Error logging object', { error: e.message });
    }

    // Common patterns we'll verify via logs:
    // - { html: "<section ...>...</section>" }
    // - { section: "<...>" } or { content: "<...>" }
    // - { "section-id": "<html...>" } (nested by section ID)
    const candidates = ['html', 'section', 'content', 'markup', sectionId];
    for (const k of candidates) {
      if (typeof maybeHtml[k] === 'string') {
        dbg('H3', 'cart-page.js:coerceSectionHtml', 'coerced html from field', { sectionId, field: k, len: maybeHtml[k].length });
        return maybeHtml[k];
      }
    }

    // If object is a map where one value is the HTML string, pick first string value.
    try {
      const entries = Object.entries(maybeHtml);
      for (const [k, v] of entries) {
        if (typeof v === 'string' && v.length > 0) {
          dbg('H3', 'cart-page.js:coerceSectionHtml', 'coerced html from map value', { sectionId, key: k, len: v.length });
          return v;
        }
        // Check if value is an object with nested HTML
        if (v && typeof v === 'object') {
          const nestedCandidates = ['html', 'section', 'content', 'markup'];
          for (const nk of nestedCandidates) {
            if (typeof v[nk] === 'string') {
              dbg('H3', 'cart-page.js:coerceSectionHtml', 'coerced html from nested field', { sectionId, key: k, nestedKey: nk, len: v[nk].length });
              return v[nk];
            }
          }
        }
      }
    } catch (_) {}

    dbg('H3', 'cart-page.js:coerceSectionHtml', 'unable to coerce html', { sectionId, keys: Object.keys(maybeHtml).slice(0, 25) });
    return null;
  }

  function replaceSection(name, html) {
    const current = document.querySelector(`[data-cart-section="${name}"]`);
    dbg('H3', 'cart-page.js:replaceSection', 'replaceSection start', {
      name,
      hasCurrent: !!current,
      htmlType: typeof html,
      htmlLen: (typeof html === 'string' ? html.length : null)
    });
    if (!current) {
      dbg('H3', 'cart-page.js:replaceSection', 'ERROR: current element not found', { name });
      return;
    }
    const coerced = coerceSectionHtml(html, name);
    if (!coerced) {
      dbg('H3', 'cart-page.js:replaceSection', 'ERROR: could not coerce HTML', { name });
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = coerced.trim();
    
    // Try multiple strategies to find the replacement element
    let next = wrapper.querySelector(`[data-cart-section="${name}"]`);
    
    if (!next) {
      // Fallback: try finding by section class or tag
      dbg('H3', 'cart-page.js:replaceSection', 'Trying fallback selectors', { name });
      next = wrapper.querySelector('.cart-page--items, .cart-page--footer') || wrapper.firstElementChild;
    }
    
    dbg('H3', 'cart-page.js:replaceSection', 'replaceSection resolved next', {
      name,
      nextTag: next ? next.tagName : null,
      nextHasAttr: next ? next.hasAttribute && next.hasAttribute('data-cart-section') : false,
      nextDataAttr: next ? next.getAttribute('data-cart-section') : null
    });
    
    if (!next) {
      dbg('H3', 'cart-page.js:replaceSection', 'ERROR: next element not found', { name });
      return;
    }
    
    dbg('H3', 'cart-page.js:replaceSection', 'Replacing element', { name });
    current.replaceWith(next);
    dbg('H3', 'cart-page.js:replaceSection', 'Replace complete', { name });
  }

  let updateTimer = null;
  let isUpdating = false;
  let queued = null;

  async function runUpdate(line, quantity) {
    // Coalesce updates (avoid race conditions while replacing DOM)
    queued = { line, quantity };
    if (isUpdating) return;

    isUpdating = true;
    document.documentElement.classList.add('cart-page-is-updating');
    dbg('H4', 'cart-page.js:runUpdate', 'runUpdate start', { line, quantity, cartUrl: CART_URL });

    try {
      while (queued) {
        const nextJob = queued;
        queued = null;

        // Call the cart change API
        await changeLine(nextJob.line, nextJob.quantity);
        
        // Fetch fresh sections
        const sections = await fetchSections();
        
        // Check if sections are null (happens when cart becomes empty or on some Shopify setups)
        if (sections['cart-items'] === null || sections['cart-footer'] === null) {
          dbg('H4', 'cart-page.js:runUpdate', 'Sections returned null, reloading page');
          window.location.reload();
          return;
        }
        
        // Try to replace sections
        replaceSection('main-cart-items', sections['cart-items']);
        replaceSection('main-cart-footer', sections['cart-footer']);

        // Let other scripts (including BSS) react
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Re-bind handlers after DOM replacement
        bind();
      }
    } catch (e) {
      dbg('H5', 'cart-page.js:runUpdate', 'runUpdate error -> reload', { message: e && e.message ? e.message : String(e) });
      window.location.href = CART_URL;
    } finally {
      isUpdating = false;
      document.documentElement.classList.remove('cart-page-is-updating');
    }
  }

  function scheduleUpdate(line, quantity) {
    if (updateTimer) window.clearTimeout(updateTimer);
    dbg('H4', 'cart-page.js:scheduleUpdate', 'scheduleUpdate', { line, quantity });
    updateTimer = window.setTimeout(() => runUpdate(line, quantity), 350);
  }

  function bind() {
    dbg('H4', 'cart-page.js:bind', 'bind start', {
      inputs: document.querySelectorAll('[data-cart-qty-input]').length,
      footer: !!document.querySelector('[data-cart-section="main-cart-footer"]'),
      items: !!document.querySelector('[data-cart-section="main-cart-items"]')
    });
    // Quantity input
    document.querySelectorAll('[data-cart-qty-input]').forEach((input) => {
      if (input.dataset.bound === '1') return;
      input.dataset.bound = '1';

      const line = parseInt(input.getAttribute('data-line') || '0', 10);
      if (!line) return;

      input.addEventListener('input', () => {
        const qty = clampToMax(line, clampQty(input.value));
        input.value = String(qty);
        scheduleUpdate(line, qty);
      });

      input.addEventListener('change', () => {
        const qty = clampToMax(line, clampQty(input.value));
        input.value = String(qty);
        scheduleUpdate(line, qty);
      });
    });

    // Plus/minus buttons
    document.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';

      const line = parseInt(btn.getAttribute('data-line') || '0', 10);
      if (!line) return;

      btn.addEventListener('click', () => {
        const input = getLineInput(line);
        if (!input) return;
        const current = clampQty(input.value);
        const rawNext = btn.hasAttribute('data-qty-plus') ? current + 1 : Math.max(0, current - 1);
        const next = clampToMax(line, rawNext);
        input.value = String(next);
        scheduleUpdate(line, next);
      });
    });

    // Remove links
    document.querySelectorAll('[data-cart-remove]').forEach((link) => {
      if (link.dataset.bound === '1') return;
      link.dataset.bound = '1';

      const line = parseInt(link.getAttribute('data-line') || '0', 10);
      if (!line) return;

      link.addEventListener('click', (e) => {
        e.preventDefault();
        runUpdate(line, 0);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();


